// StudyDost — Landing Page (Professional Redesign)
// frontend/src/pages/LandingPage.js

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Floating question cards data
const QUESTION_CARDS = [
  { q: 'What are Newton\'s 3 laws of motion?', subject: 'Physics', emoji: '⚡', color: '#4F46E5' },
  { q: 'What is photosynthesis?', subject: 'Biology', emoji: '🌿', color: '#059669' },
  { q: 'Solve: 2x² + 5x - 3 = 0', subject: 'Math', emoji: '🧮', color: '#DC2626' },
  { q: 'Capital of Rajasthan?', subject: 'GK', emoji: '🗺️', color: '#D97706' },
  { q: 'What is the water cycle?', subject: 'Science', emoji: '💧', color: '#0891B2' },
  { q: 'What did Shakespeare write?', subject: 'English', emoji: '📖', color: '#7C3AED' },
];

const STATS = [
  { num: '5', unit: 'sec', label: 'Average Response Time' },
  { num: '10K+', unit: '', label: 'Questions Answered' },
  { num: '98%', unit: '', label: 'Student Satisfaction' },
  { num: '₹99', unit: '', label: 'Monthly Plan' },
];

const FEATURES = [
  {
    emoji: '📸',
    title: 'Photo Questions',
    desc: 'Snap a photo of your homework — no typing needed. Our OCR reads it instantly.',
    color: '#F59E0B',
  },
  {
    emoji: '🎤',
    title: 'Voice Questions',
    desc: 'Ask questions with your voice. Whisper AI understands and answers in seconds.',
    color: '#6366F1',
  },
  {
    emoji: '🖼️',
    title: 'Visual Diagrams',
    desc: 'Get relevant diagrams alongside answers for Biology, Physics and Geography.',
    color: '#10B981',
  },
  {
    emoji: '👨‍👩‍👦',
    title: 'Parent Reports',
    desc: 'Parents receive a weekly study report on WhatsApp every Sunday — completely free.',
    color: '#EF4444',
  },
  {
    emoji: '🧮',
    title: 'Step-by-Step Math',
    desc: 'Every calculation shown clearly — from formula to final answer, no step skipped.',
    color: '#8B5CF6',
  },
  {
    emoji: '📚',
    title: 'CBSE/ICSE Aligned',
    desc: 'All answers aligned to NCERT syllabus — Class 1 to 12, all subjects.',
    color: '#0EA5E9',
  },
];

const PLANS = [
  {
    name: 'Monthly',
    price: '₹99',
    originalPrice: '₹199',
    period: '/month',
    desc: 'Less than a chai per day',
    features: ['Unlimited questions', 'Visual diagrams 🖼️', 'Voice questions 🎤', 'Flashcard revision', 'Parent reports'],
    cta: 'Start Monthly',
    highlight: false,
    color: '#4F46E5',
  },
  {
    name: 'Quarterly',
    price: '₹249',
    originalPrice: '₹499',
    period: '/3 months',
    desc: 'Most popular — ₹83/month',
    features: ['Everything in Monthly', 'Save 37% vs monthly', 'Best for exam prep', 'Question history', 'Priority answers'],
    cta: 'Start Quarterly',
    highlight: true,
    color: '#059669',
    badge: '⭐ Most Popular',
  },
  {
    name: 'Annual',
    price: '₹499',
    originalPrice: '₹999',
    period: '/year',
    desc: 'Best value — ₹42/month',
    features: ['Everything in Quarterly', 'Save 55% vs monthly', 'Full academic year', 'Exam readiness tracker', 'White-label homework PDF'],
    cta: 'Best Value',
    highlight: false,
    color: '#D97706',
    badge: 'Save 55%',
  },
];

const TESTIMONIALS = [
  {
    name: 'Priya Sharma',
    role: 'Class 10, DPS Noida',
    text: 'Board exam ki preparation mein bahut help mili. Math ke steps clearly dikhata hai!',
    avatar: 'P',
    color: '#4F46E5',
    stars: 5,
  },
  {
    name: 'Rajesh Kumar',
    role: 'Parent, Gurgaon',
    text: 'Mere bete ki Sunday report dekhta hoon — pata chalta hai kahan weak hai. Koi aur app nahi deta yeh!',
    avatar: 'R',
    color: '#059669',
    stars: 5,
  },
  {
    name: 'Ananya Singh',
    role: 'Class 8, Kendriya Vidyalaya',
    text: 'Photo kheench ke bhejti hoon aur 5 second mein jawab aa jata hai. Magic hai yaar!',
    avatar: 'A',
    color: '#D97706',
    stars: 5,
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [activeCard, setActiveCard] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCard(c => (c + 1) % QUESTION_CARDS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={styles.page}>

      {/* ── Nav ── */}
      <nav style={{
        ...styles.nav,
        background: scrollY > 40 ? 'rgba(255,255,255,0.95)' : 'transparent',
        backdropFilter: scrollY > 40 ? 'blur(12px)' : 'none',
        boxShadow: scrollY > 40 ? '0 1px 20px rgba(0,0,0,0.08)' : 'none',
      }}>
        <div style={styles.navInner}>
          <div style={styles.navLogo}>
            <img src="/studydost_logo.png" alt="StudyDost" style={{ height: 50, width: 'auto' }} />
          </div>
          <div style={styles.navLinks}>
            <a href="#features" style={styles.navLink}>Features</a>
            <a href="#plans" style={styles.navLink}>Plans</a>
            <a href="/parent" style={styles.navLink}>Parent Portal</a>
            <a href="/teacher" style={styles.navLink}>Teacher Portal</a>
            <a href="/books" style={styles.navLink}>Books</a>

            
          </div>
          <div style={styles.navCtas}>
            <button style={styles.navLogin} onClick={() => navigate('/login')}>Student Login</button>
            <button style={styles.navCta} onClick={() => navigate('/login')}>Try Free</button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section ref={heroRef} style={styles.hero}>
        {/* Background mesh */}
        <div style={styles.heroBg} />
        <div style={styles.heroGlow1} />
        <div style={styles.heroGlow2} />

        <div style={styles.heroContent}>
          <div style={styles.heroBadge}>
            <span style={styles.heroBadgeDot} />
            <span>India's WhatsApp homework tutor · 5 questions free daily</span>
          </div>

          <h1 style={styles.heroTitle}>
            Homework help<br />
            <span style={styles.heroTitleAccent}>in the time it<br />takes to ask.</span>
          </h1>

          <p style={styles.heroSubtitle}>
            Type, snap, or speak any question — get a step-by-step answer in seconds.<br />
            CBSE, ICSE  &amp; State Boards aligned, Class 1–12, Hindi + English.
          </p>

          <div style={styles.heroCtas}>
            <a
              href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || "15556317048"}?text=Hi`}
              target="_blank"
              rel="noreferrer"
              style={styles.heroCtaPrimary}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Start on WhatsApp
            </a>
            <button style={styles.heroCtaSecondary} onClick={() => navigate('/login')}>
              Open web app →
            </button>
          </div>

          <div style={styles.trustPills}>
            <span style={styles.trustPill}>✓ 10 questions free daily</span>
            <span style={styles.trustPill}>✓ No app needed</span>
            <span style={styles.trustPill}>✓ Parent reports</span>
          </div>
        </div>

        {/* Phone mockup */}
        <div style={styles.heroPhone}>
          <div style={styles.phoneMockup}>
            {/* Phone frame */}
            <div style={styles.phoneFrame}>
              {/* Status bar */}
              <div style={styles.phoneStatusBar}>
                <span style={{fontSize:11,fontWeight:600}}>9:41</span>
                <div style={{display:'flex',gap:4,alignItems:'center'}}>
                  <svg width="14" height="10" viewBox="0 0 18 12" fill="#0B1020" data-om-id="jsx:/https:/019df843-33fd-73b7-a165-d1afc0d7103a.claudeusercontent.com/v1/design/projects/019df843-33fd-73b7-a165-d1afc0d7103a/serve/components/primitives.jsx:7760:76:13"><rect x="0" y="8" width="3" height="4" rx="1" data-om-id="jsx:/https:/019df843-33fd-73b7-a165-d1afc0d7103a.claudeusercontent.com/v1/design/projects/019df843-33fd-73b7-a165-d1afc0d7103a/serve/components/primitives.jsx:7847:76:100"></rect><rect x="5" y="5" width="3" height="7" rx="1" data-om-id="jsx:/https:/019df843-33fd-73b7-a165-d1afc0d7103a.claudeusercontent.com/v1/design/projects/019df843-33fd-73b7-a165-d1afc0d7103a/serve/components/primitives.jsx:7894:76:147"></rect><rect x="10" y="2" width="3" height="10" rx="1" data-om-id="jsx:/https:/019df843-33fd-73b7-a165-d1afc0d7103a.claudeusercontent.com/v1/design/projects/019df843-33fd-73b7-a165-d1afc0d7103a/serve/components/primitives.jsx:7941:76:194"></rect><rect x="15" y="0" width="3" height="12" rx="1" data-om-id="jsx:/https:/019df843-33fd-73b7-a165-d1afc0d7103a.claudeusercontent.com/v1/design/projects/019df843-33fd-73b7-a165-d1afc0d7103a/serve/components/primitives.jsx:7990:76:243"></rect></svg>
                  <svg width="22" height="11" viewBox="0 0 28 12" fill="none" data-om-id="jsx:/https:/019df843-33fd-73b7-a165-d1afc0d7103a.claudeusercontent.com/v1/design/projects/019df843-33fd-73b7-a165-d1afc0d7103a/serve/components/primitives.jsx:8058:77:13"><rect x="0" y="0.5" width="24" height="11" rx="3" stroke="#0B1020" data-om-id="jsx:/https:/019df843-33fd-73b7-a165-d1afc0d7103a.claudeusercontent.com/v1/design/projects/019df843-33fd-73b7-a165-d1afc0d7103a/serve/components/primitives.jsx:8118:77:73"></rect><rect x="2" y="2" width="18" height="8" rx="1" fill="#0B1020" data-om-id="jsx:/https:/019df843-33fd-73b7-a165-d1afc0d7103a.claudeusercontent.com/v1/design/projects/019df843-33fd-73b7-a165-d1afc0d7103a/serve/components/primitives.jsx:8210:77:165"></rect><rect x="25" y="3.5" width="2" height="5" rx="1" fill="#0B1020" data-om-id="jsx:/https:/019df843-33fd-73b7-a165-d1afc0d7103a.claudeusercontent.com/v1/design/projects/019df843-33fd-73b7-a165-d1afc0d7103a/serve/components/primitives.jsx:8297:77:252"></rect></svg>
                </div>
              </div>
              {/* WhatsApp-style chat header */}
              <div style={styles.phoneChatHeader}>
                <img src="/studydost_logo.png" alt="StudyDost" style={{width:34,height:34,borderRadius:'50%',objectFit:'contain',background:'#EEF2FF'}} />
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:'#f2f2f2'}}>StudyDost</div>
                  <div style={{fontSize:11,color:'#25D366',fontWeight:500}}>● online · class 10</div>
                </div>
              </div>
              {/* Chat messages */}
              <div style={styles.phoneChatArea}>
                {/* User message */}
                <div style={styles.phoneUserMsg}>What is photosynthesis?</div>
                {/* Bot response */}
                <div style={styles.phoneBotMsg}>
                  <div style={{fontSize:11,fontWeight:700,color:'#4F46E5',marginBottom:4}}>π SCIENCE</div>
                  <div style={{fontSize:12,fontWeight:700,marginBottom:4}}>Photosynthesis — quick version</div>
                  <div style={{fontSize:11,lineHeight:1.5,color:'#374151'}}>
                    Plants <strong>make food</strong> from sunlight. The process turns CO₂ + H₂O into glucose + O₂.
                  </div>
                  <div style={{marginTop:6,background:'#F0FDF4',borderRadius:6,padding:'4px 8px',fontSize:10,color:'#166534',fontWeight:600}}>
                    6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂
                  </div>
                  <div style={{marginTop:4,fontSize:10,color:'#4F46E5'}}>+ diagram attached</div>
                </div>
                {/* XP chip */}
                <div style={{display:'flex',justifyContent:'flex-end',marginTop:4}}>
                  <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:8,padding:'3px 8px',fontSize:10,color:'#92400E',fontWeight:600}}>
                    ✨ +10 XP · 1-day streak
                  </div>
                </div>
                {/* Input bar */}
                <div style={styles.phoneInputBar}>
                  <div style={{flex:1,background:'#F3F4F6',borderRadius:20,padding:'6px 12px',fontSize:11,color:'#9CA3AF'}}>Type a question...</div>
                  <div style={{fontSize:14}}>📷</div>
                  <div style={{fontSize:14}}>🎤</div>
                </div>
              </div>
            </div>
            {/* Floating badge */}
            <div style={styles.phoneBadgeTop}>
              🔥 24x7 Live 📱 Tutor
            </div>
            <div style={styles.phoneBadge}>
              📤 Text, Image or Voice
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section style={styles.statsSection}>
        <div style={styles.statsGrid}>
          {STATS.map((s, i) => (
            <div key={i} style={styles.statCard}>
              <div style={styles.statNum}>
                {s.num}<span style={styles.statUnit}>{s.unit}</span>
              </div>
              <div style={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section style={styles.section}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionTag}>🚀 Simple Process</div>
          <h2 style={styles.sectionTitle}>Get Answers in 3 Simple Steps</h2>
          <p style={styles.sectionDesc}>So simple it works the very first time</p>

          <div style={styles.stepsGrid}>
            {[
              { num: '01', emoji: '💬', title: 'Ask a Question', desc: 'On WhatsApp or the web — type it, photo it, or say it. Anything goes.', color: '#4F46E5' },
              { num: '02', emoji: '🤖', title: 'AI Understands', desc: 'GPT-4o instantly reads your question and prepares an answer aligned to your syllabus.', color: '#059669' },
              { num: '03', emoji: '✅', title: 'Get Your Answer', desc: 'Step-by-step explanation, diagrams, and memory tips — all in under 30 seconds.', color: '#D97706' },
            ].map((step, i) => (
              <div key={i} style={styles.stepCard}>
                <div style={{ ...styles.stepNum, color: step.color, borderColor: step.color + '30' }}>
                  {step.num}
                </div>
                <div style={{ ...styles.stepEmoji }}>{step.emoji}</div>
                <h3 style={styles.stepTitle}>{step.title}</h3>
                <p style={styles.stepDesc}>{step.desc}</p>
                {i < 2 && <div style={{ ...styles.stepArrow, color: step.color }}>→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" style={{ ...styles.section, background: '#F8FAFF' }}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionTag}>✨ Features</div>
          <h2 style={styles.sectionTitle}>Not Only Answers —<br />Complete Learning Experience</h2>

          <div style={styles.featuresGrid}>
            {FEATURES.map((f, i) => (
              <div key={i} style={styles.featureCard}>
                <div style={{ ...styles.featureIcon, background: f.color + '15' }}>
                  <span style={{ fontSize: 28 }}>{f.emoji}</span>
                </div>
                <h3 style={styles.featureTitle}>{f.title}</h3>
                <p style={styles.featureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Plans ── */}
      <section id="plans" style={styles.section}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionTag}>💎 Pricing</div>
          <h2 style={styles.sectionTitle}>One plan for everyone</h2>
          <p style={styles.sectionDesc}>No hidden charges. Upgrade anytime.</p>

          <div style={styles.plansGrid}>
            {PLANS.map((plan, i) => (
              <div key={i} style={{
                ...styles.planCard,
                border: plan.highlight ? `2px solid ${plan.color}` : '1px solid #E2E8F0',
                boxShadow: plan.highlight ? `0 20px 60px ${plan.color}25` : '0 4px 20px rgba(0,0,0,0.06)',
                transform: plan.highlight ? 'scale(1.04)' : 'scale(1)',
              }}>
                {plan.badge && (
                  <div style={{ ...styles.planBadge, background: plan.color }}>
                    {plan.badge}
                  </div>
                )}
                <div style={{ ...styles.planName, color: plan.color }}>{plan.name}</div>
                <div style={styles.planPrice}>
                  {plan.price}
                  <span style={styles.planPeriod}>{plan.period}</span>
                </div>
                {plan.originalPrice && (
                  <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 4 }}>
                    <span style={{ textDecoration: 'line-through' }}>{plan.originalPrice}</span>
                    <span style={{ color: '#059669', fontWeight: 700, marginLeft: 6 }}>🎉 Launch Offer</span>
                  </div>
                )}
                <p style={styles.planDesc}>{plan.desc}</p>
                <div style={styles.planFeatures}>
                  {plan.features.map((f, j) => (
                    <div key={j} style={styles.planFeature}>
                      <span style={{ ...styles.planCheck, color: plan.color }}>✓</span>
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
                <button
                  style={{
                    ...styles.planCta,
                    background: plan.highlight ? plan.color : 'transparent',
                    color: plan.highlight ? '#FFFFFF' : plan.color,
                    border: `2px solid ${plan.color}`,
                  }}
                  onClick={() => navigate('/login')}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          <div style={styles.perQuestionNote}>
            <span style={styles.perQBadge}>🆓</span>
            Start with <strong>10 free questions daily</strong> — no card needed!
          </div>
        </div>
      </section>

      {/* ── Testimonials ── (commented out — add genuine ones later) */}
      {false && <section id="testimonials" style={{ ...styles.section, background: '#F8FAFF' }}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionTag}>❤️ Reviews</div>
          <h2 style={styles.sectionTitle}>Students Aur Parents<br />Ki Rai</h2>

          <div style={styles.testimonialsGrid}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={styles.testimonialCard}>
                <div style={styles.testimonialStars}>
                  {'⭐'.repeat(t.stars)}
                </div>
                <p style={styles.testimonialText}>"{t.text}"</p>
                <div style={styles.testimonialAuthor}>
                  <div style={{ ...styles.testimonialAvatar, background: t.color }}>
                    {t.avatar}
                  </div>
                  <div>
                    <div style={styles.testimonialName}>{t.name}</div>
                    <div style={styles.testimonialRole}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>}

      {/* ── CTA Banner ── */}
      <section style={styles.ctaBanner}>
        <div style={styles.ctaBannerGlow} />
        <div style={styles.ctaContent}>
          <h2 style={styles.ctaTitle}>Start Learning Today</h2>
          <p style={styles.ctaDesc}>
            No download. No signup required.<br />
            Just open WhatsApp and say "Hi".
          </p>
          <div style={styles.ctaButtons}>
            <a
              href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || "15556317048"}?text=Hi`}
              target="_blank"
              rel="noreferrer"
              style={styles.ctaPrimary}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Start on WhatsApp
            </a>
            <button style={styles.ctaSecondary} onClick={() => navigate('/login')}>
              Try the Web App
            </button>
            <button
              style={{...styles.ctaSecondary, background:'#dcfce7', color:'#15803d', border:'2px solid #16a34a'}}
              onClick={() => navigate('/books')}
            >
              📚 NCERT Books Free
            </button>
          </div>
          <p style={styles.ctaNote}>🔒 No spam • Cancel anytime • 10 questions daily FREE forever</p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <div style={styles.footerLogo}>
            <img src="/studydost_logo.png" alt="StudyDost" style={{ height: 40, width: 'auto' }} />
          </div>

          <div style={styles.footerLinks}>
            <a href="/about" style={styles.footerLink}>About Us</a>
            <a href="/parent" style={styles.footerLink}>Parent Portal</a>
            <a href="/teacher" style={styles.footerLink}>Teacher Portal</a>
            <a href="/plans" style={styles.footerLink}>Plans</a>
            <a href="mailto:hello@studydost.ai" style={styles.footerLink}>Contact</a>
          </div>

          <div style={styles.footerLinks} style2={{ marginTop: 8 }}>
            <a href="/privacy" style={{ ...styles.footerLink, fontSize: 12, color: '#94A3B8' }}>Privacy Policy</a>
            <span style={{ color: '#334155', fontSize: 12 }}>·</span>
            <a href="/terms" style={{ ...styles.footerLink, fontSize: 12, color: '#94A3B8' }}>Terms & Conditions</a>
            <span style={{ color: '#334155', fontSize: 12 }}>·</span>
            <a href="/refund" style={{ ...styles.footerLink, fontSize: 12, color: '#94A3B8' }}>Refund Policy</a>
          </div>

          <div style={styles.footerCopy}>
            © {new Date().getFullYear()} Let's Redefine Private Limited • studydost.ai
          </div>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; }

        html { scroll-behavior: smooth; }

        body { font-family: 'DM Sans', sans-serif; }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.3); }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .hero-content > * {
          animation: fadeUp 0.7s ease forwards;
        }
        .hero-content > *:nth-child(1) { animation-delay: 0.1s; opacity: 0; }
        .hero-content > *:nth-child(2) { animation-delay: 0.2s; opacity: 0; }
        .hero-content > *:nth-child(3) { animation-delay: 0.3s; opacity: 0; }
        .hero-content > *:nth-child(4) { animation-delay: 0.4s; opacity: 0; }
        .hero-content > *:nth-child(5) { animation-delay: 0.5s; opacity: 0; }

        a { text-decoration: none; color: inherit; }

        section { animation: fadeUp 0.6s ease; }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FFFFFF',
    fontFamily: "'DM Sans', sans-serif",
    overflowX: 'hidden',
  },

  // ── Nav ──
  nav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    background: '#FFFFFF',
    transition: 'all 0.3s ease',
  },
  navInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
  },
  navLogoEmoji: { fontSize: 28 },
  navLogoText: {
    fontSize: 22,
    fontWeight: 800,
    fontFamily: "'Sora', sans-serif",
    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  navLinks: {
    display: 'flex',
    gap: 32,
    '@media (max-width: 768px)': { display: 'none' },
  },
  navLink: {
    fontSize: 15,
    fontWeight: 500,
    color: '#475569',
    textDecoration: 'none',
    transition: 'color 0.2s',
  },
  navCtas: { display: 'flex', gap: 12, alignItems: 'center' },
  navLogin: {
    background: 'none',
    border: 'none',
    fontSize: 15,
    fontWeight: 600,
    color: '#4F46E5',
    cursor: 'pointer',
    padding: '8px 16px',
  },
  navCta: {
    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 100,
    padding: '10px 22px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(79,70,229,0.3)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },

  // ── Hero ──
  hero: {
    minHeight: '100vh',
    display: 'grid',
    gridTemplateColumns:'1.1fr 1fr',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    paddingTop: 80,
    paddingBottom: 60,
    gap:48,
  },
  heroBg: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(160deg, #EEF2FF 0%, #F8FAFF 50%, #FFF7ED 100%)',
    zIndex: 0,
  },
  heroGlow1: {
    position: 'absolute',
    top: '10%',
    left: '5%',
    width: 500,
    height: 500,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(79,70,229,0.12) 0%, transparent 70%)',
    zIndex: 0,
  },
  heroGlow2: {
    position: 'absolute',
    bottom: '10%',
    right: '5%',
    width: 400,
    height: 400,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(217,119,6,0.1) 0%, transparent 70%)',
    zIndex: 0,
  },
  heroContent: {
    flex: 1,
    maxWidth: 750,
    padding: '0 24px 0 calc((100vw - 1200px) / 2 + 24px)',
    position: 'relative',
    zIndex: 1,
    className: 'hero-content',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(79,70,229,0.08)',
    border: '1px solid rgba(79,70,229,0.2)',
    color: '#4F46E5',
    borderRadius: 100,
    padding: '6px 16px',
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 24,
  },
  heroBadgeDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#4F46E5',
    animation: 'pulse-dot 2s infinite',
  },
  heroTitle: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 'clamp(44px, 6vw, 62px)',
    fontWeight: 800,
    lineHeight: 1.1,
    color: '#0F172A',
    marginBottom: 20,
    letterSpacing: '-0.02em',
  },
  heroTitleAccent: {
    background: 'linear-gradient(135deg, #4F46E5 0%, #D97706 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  heroSubtitle: {
    fontSize: 18,
    lineHeight: 1.7,
    color: '#475569',
    marginBottom: 36,
    fontWeight: 400,
  },
  heroCtas: {
    display: 'flex',
    gap: 14,
    flexWrap: 'nowrap',
    marginBottom: 20,
  },
  heroCtaPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    background: '#25D366',
    color: '#FFFFFF',
    borderRadius: 20,
    padding: '16px 28px',
    fontSize: 16,
    fontWeight: 700,
    textDecoration: 'none',
    boxShadow: '0 8px 30px rgba(37,211,102,0.35)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  heroCtaSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: 'transparent',
    color: '#4F46E5',
    border: '2px solid #4F46E5',
    borderRadius: 20,
    padding: '14px 26px',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
  },
  trustPills: { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 },
  trustPill: { fontSize: 13, color: '#4B5563', display: 'flex', alignItems: 'center', gap: 4 },
  statsBar: { background: '#0F172A', padding: '32px 24px' },
  statsBarInner: { maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, textAlign: 'center' },
  statItem: { },
  statsBarValue: { fontSize: 36, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em', marginBottom: 4 },
  statsBarLabel: { fontSize: 11, fontWeight: 600, color: '#64748B', letterSpacing: '0.08em' },
  heroNote: {
    fontSize: 13,
    color: '#94A3B8',
    fontWeight: 500,
  },

  // ── Hero Cards ──
  heroPhone: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '20px 0',
  },
  phoneMockup: {
    position: 'relative',
    width: 300,
    height: 580,
    background: 'rgb(11, 16, 32)',
    borderRadius: 44,
    padding: 8,
    boxShadow: 'rgba(11, 16, 32, 0.08) 0px 1px 2px, rgba(11, 16, 32, 0.18) 0px 30px 60px',

  },
  phoneFrame: {
    border: '6px solid #0F172A',
    boxShadow: 'rgba(0, 0, 0, 0.3) 0px 40px 80px',
    position: 'absolute',
    inset: 8,
    borderRadius: 36,
    overflow:'hidden',
    background: 'rgb(255, 255, 255)',

  },
  phoneStatusBar: {
    color: '#FFF',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 36,
    padding: '8px 22px 0px',
    fontSize: 12,
    fontWeight: 600,
    color: 'rgb(11, 16, 32)',
    position: 'relative',
    zIndex: 5,
  },
  phoneChatHeader: {
    background: '#075E54',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
  },
  phoneChatArea: {
    background: '#ECE5DD',
    padding: '12px 8px 8px',
    minHeight: 280,
    display: 'flex',
    flexDirection: 'column',
    gap: 58,
    overflow: 'hidden',
  },
  phoneUserMsg: {
    background: '#DCF8C6',
    alignSelf: 'flex-end',
    borderRadius: '12px 12px 2px 12px',
    padding: '6px 10px',
    fontSize: 11,
    color: '#111',
    maxWidth: '80%',
  },
  phoneBotMsg: {
    background: '#FFF',
    alignSelf: 'flex-start',
    borderRadius: '2px 12px 12px 12px',
    padding: '8px 10px',
    fontSize: 11,
    maxWidth: '90%',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  phoneInputBar: {
    background: '#FFF',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    marginTop: 4,
    borderRadius: 20,
  },
    phoneBadgeTop: {
    position: 'absolute',
    top: 0,
    left: -105,
    background: '#FFF',
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    animation: 'float 3s ease-in-out infinite',
  },
  phoneBadge: {
    position: 'absolute',
    bottom: -11,
    right: -45,
    background: '#FFF',
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    animation: 'float 3s ease-in-out infinite',

  },
  heroCards: {
    flex: 1,
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    padding: '0 24px',
    maxWidth: 480,
    minHeight: 400,
  },
  heroCardStack: {
    position: 'relative',
    width: '100%',
    height: 180,
  },
  floatingCard: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    background: '#FFFFFF',
    borderRadius: 20,
    padding: '20px 24px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
    border: '1px solid rgba(255,255,255,0.8)',
  },
  cardSubjectTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: 20,
    marginBottom: 10,
  },
  cardQuestion: {
    fontSize: 16,
    fontWeight: 600,
    color: '#0F172A',
    marginBottom: 12,
    lineHeight: 1.4,
  },
  cardAnswer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: '#94A3B8',
  },
  cardAnswerDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#25D366',
    animation: 'pulse-dot 1.2s infinite',
  },
  answerCard: {
    width: '100%',
    background: '#FFFFFF',
    borderRadius: 20,
    padding: '18px 22px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
    border: '1px solid #E8F5E9',
    borderLeft: '4px solid #25D366',
  },
  answerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  answerLogo: { fontSize: 28 },
  answerName: { fontSize: 14, fontWeight: 700, color: '#0F172A' },
  answerTime: { fontSize: 11, color: '#94A3B8' },
  answerText: { fontSize: 15, color: '#1E293B', lineHeight: 1.6 },
  answerFact: { fontSize: 13, color: '#64748B' },

  // ── Stats ──
  statsSection: {
    background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
    padding: '48px 24px',
  },
  statsGrid: {
    maxWidth: 1000,
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 24,
  },
  statCard: {
    textAlign: 'center',
    padding: '12px',
  },
  statNum: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 44,
    fontWeight: 800,
    color: '#FFFFFF',
    lineHeight: 1,
    marginBottom: 6,
  },
  statUnit: { fontSize: 24 },
  statLabel: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.7)',
    fontWeight: 500,
    lineHeight: 1.4,
  },

  // ── Sections ──
  section: { padding: '80px 24px' },
  sectionInner: { maxWidth: 1200, margin: '0 auto' },
  sectionTag: {
    display: 'inline-block',
    background: '#EEF2FF',
    color: '#4F46E5',
    fontSize: 13,
    fontWeight: 700,
    padding: '6px 14px',
    borderRadius: 100,
    marginBottom: 16,
    letterSpacing: '0.02em',
  },
  sectionTitle: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 'clamp(32px, 4vw, 48px)',
    fontWeight: 800,
    color: '#0F172A',
    marginBottom: 14,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
  },
  sectionDesc: {
    fontSize: 18,
    color: '#64748B',
    marginBottom: 56,
    maxWidth: 500,
    lineHeight: 1.6,
  },

  // ── Steps ──
  stepsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 32,
    position: 'relative',
  },
  stepCard: {
    background: '#FFFFFF',
    borderRadius: 24,
    padding: '36px 28px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
    border: '1px solid #F1F5F9',
    position: 'relative',
    transition: 'transform 0.3s, box-shadow 0.3s',
  },
  stepNum: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 52,
    fontWeight: 900,
    border: '2px solid',
    width: 72,
    height: 72,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
    marginBottom: 16,
    letterSpacing: '-0.04em',
    background: 'rgba(255,255,255,0.9)',
  },
  stepEmoji: { fontSize: 36, marginBottom: 12 },
  stepTitle: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 20,
    fontWeight: 700,
    color: '#0F172A',
    marginBottom: 10,
  },
  stepDesc: { fontSize: 15, color: '#64748B', lineHeight: 1.65 },
  stepArrow: {
    position: 'absolute',
    right: -22,
    top: '50%',
    fontSize: 28,
    fontWeight: 900,
    zIndex: 1,
    transform: 'translateY(-50%)',
  },

  // ── Features ──
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
  },
  featureCard: {
    background: '#FFFFFF',
    borderRadius: 20,
    padding: '28px',
    border: '1px solid #F1F5F9',
    transition: 'transform 0.3s, box-shadow 0.3s',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  },
  featureIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  featureTitle: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 17,
    fontWeight: 700,
    color: '#0F172A',
    marginBottom: 8,
  },
  featureDesc: { fontSize: 14, color: '#64748B', lineHeight: 1.65 },

  // ── Plans ──
  plansGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
    alignItems: 'start',
  },
  planCard: {
    background: '#FFFFFF',
    borderRadius: 24,
    padding: '32px 28px',
    position: 'relative',
    transition: 'transform 0.3s',
  },
  planBadge: {
    position: 'absolute',
    top: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 700,
    padding: '4px 16px',
    borderRadius: 100,
    whiteSpace: 'nowrap',
  },
  planName: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  planPrice: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 48,
    fontWeight: 900,
    color: '#0F172A',
    lineHeight: 1,
    marginBottom: 6,
    letterSpacing: '-0.03em',
  },
  planPeriod: { fontSize: 18, fontWeight: 500, color: '#94A3B8' },
  planDesc: { fontSize: 14, color: '#64748B', marginBottom: 24, lineHeight: 1.5 },
  planFeatures: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 },
  planFeature: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#374151' },
  planCheck: { fontSize: 16, fontWeight: 800, flexShrink: 0 },
  planCta: {
    width: '100%',
    borderRadius: 12,
    padding: '14px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: "'DM Sans', sans-serif",
  },
  perQuestionNote: {
    textAlign: 'center',
    marginTop: 32,
    fontSize: 16,
    color: '#64748B',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  perQBadge: {
    background: '#FEF3C7',
    borderRadius: 100,
    padding: '4px 10px',
    fontSize: 18,
  },

  // ── Testimonials ──
  testimonialsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
  },
  testimonialCard: {
    background: '#FFFFFF',
    borderRadius: 20,
    padding: '28px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
    border: '1px solid #F1F5F9',
  },
  testimonialStars: { fontSize: 16, marginBottom: 14 },
  testimonialText: {
    fontSize: 15,
    color: '#374151',
    lineHeight: 1.7,
    fontStyle: 'italic',
    marginBottom: 20,
  },
  testimonialAuthor: { display: 'flex', alignItems: 'center', gap: 12 },
  testimonialAvatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 800,
    flexShrink: 0,
  },
  testimonialName: { fontSize: 15, fontWeight: 700, color: '#0F172A' },
  testimonialRole: { fontSize: 12, color: '#94A3B8', marginTop: 2 },

  // ── CTA Banner ──
  ctaBanner: {
    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
    padding: '80px 24px',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  ctaBannerGlow: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    height: 300,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(79,70,229,0.3) 0%, transparent 70%)',
  },
  ctaContent: { position: 'relative', zIndex: 1 },
  ctaTitle: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 'clamp(36px, 5vw, 56px)',
    fontWeight: 800,
    color: '#FFFFFF',
    marginBottom: 16,
    letterSpacing: '-0.02em',
  },
  ctaDesc: {
    fontSize: 18,
    color: '#94A3B8',
    lineHeight: 1.7,
    marginBottom: 40,
  },
  ctaButtons: {
    display: 'flex',
    gap: 16,
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: 24,
  },
  ctaPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    background: '#25D366',
    color: '#FFFFFF',
    borderRadius: 100,
    padding: '18px 32px',
    fontSize: 17,
    fontWeight: 700,
    textDecoration: 'none',
    boxShadow: '0 8px 30px rgba(37,211,102,0.3)',
  },
  ctaSecondary: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#FFFFFF',
    borderRadius: 100,
    padding: '18px 32px',
    fontSize: 17,
    fontWeight: 700,
    cursor: 'pointer',
    backdropFilter: 'blur(10px)',
  },
  ctaNote: { fontSize: 13, color: '#64748B' },

  // ── Footer ──
  footer: {
    background: '#F8FAFF',
    borderTop: '1px solid #E2E8F0',
    padding: '32px 24px',
  },
  footerInner: {
    maxWidth: 1200,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 20,
  },
  footerLogo: { display: 'flex', alignItems: 'center', gap: 12 },
  footerLogoEmoji: { fontSize: 32 },
  footerLogoText: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 18,
    fontWeight: 800,
    color: '#0F172A',
  },
  footerLogoSub: { fontSize: 12, color: '#94A3B8', marginTop: 2 },
  footerLinks: { display: 'flex', gap: 28 },
  footerLink: { fontSize: 14, color: '#64748B', textDecoration: 'none', fontWeight: 500 },
  footerCopy: { fontSize: 13, color: '#94A3B8' },
};
